import React, { useEffect, useState, useCallback } from 'react';
import { aiUsageApi } from '../api/client';

// Admin AWS Usage tab: today's estimated AI spend vs the daily cap (the hard-stop),
// a 7-day AI history, and real billed AWS cost by service (Cost Explorer, month-to-date).
export default function AwsUsage() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [capInput, setCapInput] = useState('');
  const [savingCap, setSavingCap] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await aiUsageApi.get();
      setData(res);
      setCapInput(String(res?.today?.cap_usd ?? ''));
    } catch (e: any) {
      setErr(e?.message || 'Could not load AWS usage.');
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCap = async () => {
    const v = parseFloat(capInput);
    if (!(v > 0)) { setErr('Enter a positive daily cap.'); return; }
    setSavingCap(true); setErr(''); setMsg('');
    try {
      await aiUsageApi.setCap(v);
      setMsg(`Daily AI cap set to $${v.toFixed(2)}.`);
      load();
    } catch (e: any) {
      setErr(e?.message || 'Could not save the cap.');
    }
    setSavingCap(false);
  };

  const t = data?.today;
  const billed = data?.billed;
  const hist = data?.history || [];
  const maxHist = Math.max(0.001, ...hist.map((h: any) => h.est_usd || 0));

  return (
    <div style={{ maxWidth: 900 }}>
      <h2 style={{ fontSize: 20, margin: '0 0 4px' }}>AWS Usage & AI Cost Cap</h2>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0 }}>
        Real-time estimated AI spend enforces a daily hard-stop. Billed dollars come from AWS Cost
        Explorer (account-wide, month-to-date, updates ~daily).
      </p>

      {err && <div role="alert" style={alert('#991b1b', '#fee2e2')}>{err}</div>}
      {msg && !err && <div style={alert('#166534', '#dcfce7')}>{msg}</div>}
      {loading && <div style={{ color: '#64748b', fontSize: 13 }}>Loading…</div>}

      {t && (
        <>
          {/* Today vs cap */}
          <section style={card}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: 15, color: '#0f172a' }}>Today's AI spend (estimated)</h3>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8' }}>{t.date} UTC · {t.calls} AI calls</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '10px 0 6px' }}>
              <span style={{ fontSize: 30, fontWeight: 800, color: t.blocked ? '#991b1b' : '#0f172a' }}>
                ${Number(t.est_ai_usd).toFixed(t.est_ai_usd >= 1 ? 2 : 4)}
              </span>
              <span style={{ fontSize: 14, color: '#64748b' }}>/ ${Number(t.cap_usd).toFixed(2)} cap</span>
              {t.blocked && <span style={{ marginLeft: 8, background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: 12, padding: '3px 10px', borderRadius: 999 }}>AI BLOCKED — resets midnight UTC</span>}
            </div>
            <div style={{ height: 12, background: '#e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ width: `${t.pct}%`, height: '100%', background: t.pct >= 100 ? '#dc2626' : t.pct >= 80 ? '#f59e0b' : '#0f766e', transition: 'width .4s' }} />
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{t.pct}% of daily cap used</div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>Daily AI cap (USD)</label>
              <input value={capInput} onChange={e => setCapInput(e.target.value)} type="number" min="1" step="1"
                style={{ width: 100, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }} />
              <button onClick={saveCap} disabled={savingCap}
                style={{ padding: '7px 16px', background: savingCap ? '#94a3b8' : '#4338ca', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: savingCap ? 'default' : 'pointer' }}>
                {savingCap ? 'Saving…' : 'Save cap'}
              </button>
            </div>
          </section>

          {/* 7-day AI history */}
          <section style={card}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#0f172a' }}>AI spend — last 7 days (estimated)</h3>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 120 }}>
              {hist.map((h: any) => (
                <div key={h.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{ fontSize: 10, color: '#64748b' }}>${Number(h.est_usd).toFixed(h.est_usd >= 1 ? 2 : 3)}</div>
                  <div style={{ width: '100%', background: '#0f766e', borderRadius: '4px 4px 0 0',
                                height: `${Math.max(2, (h.est_usd / maxHist) * 90)}px` }} title={`${h.calls} calls`} />
                  <div style={{ fontSize: 9, color: '#94a3b8' }}>{h.date.slice(5)}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Real billed cost */}
          <section style={card}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15, color: '#0f172a' }}>Billed AWS cost by service</h3>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8' }}>{billed?.period}</span>
            </div>
            {billed?.note ? (
              <div style={{ fontSize: 12, color: '#b45309', marginTop: 8 }}>{billed.note}</div>
            ) : (
              <>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: '8px 0 12px' }}>
                  ${Number(billed?.total || 0).toFixed(2)} <span style={{ fontSize: 13, fontWeight: 500, color: '#64748b' }}>total (account, month-to-date)</span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {(billed?.by_service || []).map((s: any) => (
                      <tr key={s.service} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '6px 8px', color: '#334155' }}>{s.service}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>${s.usd.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
              Billed figures are account-wide from AWS Cost Explorer and lag ~24h. The daily hard-stop
              uses the real-time AI estimate above (Bedrock), which is the main controllable AI cost.
            </p>
          </section>
        </>
      )}
    </div>
  );
}

const card: React.CSSProperties = { border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, background: 'white', marginTop: 16 };
const alert = (fg: string, bg: string): React.CSSProperties => ({ color: fg, background: bg, padding: '10px 14px', borderRadius: 8, marginTop: 12, fontSize: 13 });
